/**
 * Header Component
 * Requirements: 3.3
 */
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { Resume, UserPreferences } from '../types';
import {
  Menu,
  X,
  Plus,
  User,
  LogOut,
  Settings,
  FileText,
  Link,
  ChevronDown,
  Edit,
  Home,
  Info,
  Mail,
  Sparkles,
  MessageSquareHeart,
  LayoutGrid,
  Briefcase,
  MessageSquareText,
  MessageCircleQuestion,
  HelpCircle,
  Fingerprint,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { FreeGenCounter } from './FreeGenCounter';
import { recordLintHighCount, masterLintChipText } from '../utils/masterLint';
import { FeedbackModal } from './FeedbackModal';
import { useHelp } from '../help/HelpContext';
import { HOTKEY_CHIP } from '../hooks/hotkeyRules';
import version from '../../../version.txt?raw';

/**
 * Right-aligned shortcut chip on a Feature Selector item. The same key works
 * globally on every full page (see hooks/useGlobalHotkeys.ts).
 */
function HotkeyChip({ children }: { children: string }) {
  return (
    <kbd className="ml-auto pl-3 font-sans text-[11px] font-medium leading-none text-gray-400">
      {children}
    </kbd>
  );
}

/**
 * "N issues copied to every resume" — Recruiter Check BR-1 on a selector row.
 *
 * Rendered from the resume record's persisted `lint_high_count`, so the list
 * never runs a lint pass per row. No count ⇒ nothing renders, which is also
 * what every record saved before the field existed shows.
 */
function MasterLintWarning({ resume, onClick }: { resume: Resume; onClick: () => void }) {
  const count = recordLintHighCount(resume);
  if (!count) return null;
  return (
    <button
      type="button"
      data-testid="master-lint-warning"
      data-count={String(count)}
      title="These are in your master resume, so every resume you generate carries them. Click to fix."
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="mt-0.5 flex items-center gap-1 w-full text-left text-[11px] text-amber-700 hover:text-amber-900 hover:underline"
    >
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{masterLintChipText(count)}</span>
    </button>
  );
}

interface HeaderProps {
  onAddJobUrl: () => void;
  onAddJobManual: () => void;
  onAddJobEmail: () => void;
  onOpenSettings: () => void;
  onOpenWritingSample: () => void;
  onUpgrade: () => void;
  selectedResume: string | null;
  onSelectResume: (resumename: string) => void;
  onEditResume: (resumename: string) => void;
  onNewResume: () => void;
  userPreferences?: UserPreferences;
  /** True once GET /preferences has settled — see the restore effect below. */
  prefsResolved: boolean;
  onPreferencesChange?: (prefs: Partial<UserPreferences>) => void;
  resumeRefreshKey?: number;
}

export function Header({
  onAddJobUrl,
  onAddJobManual,
  onAddJobEmail,
  onOpenSettings,
  onOpenWritingSample,
  onUpgrade,
  selectedResume,
  onSelectResume,
  onEditResume,
  onNewResume,
  userPreferences,
  prefsResolved,
  onPreferencesChange: _onPreferencesChange,
  resumeRefreshKey,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const { open: openHelpPanel } = useHelp();
  const [showResumeDropdown, setShowResumeDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFeatureMenu, setShowFeatureMenu] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(true);

  const resumeDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const featureMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadResumes();
  }, [resumeRefreshKey]);

  // Restore the user's sticky resume once BOTH the resume list and the
  // preferences have arrived — waiting on `prefsResolved` is what stops the
  // first-resume default from winning the race against the stored pick. A
  // stored name that no longer exists (renamed/deleted) falls back to first,
  // as does a failed preferences fetch (`prefsResolved` is true either way).
  useEffect(() => {
    if (loadingResumes || selectedResume || resumes.length === 0 || !prefsResolved) return;
    const stored = userPreferences?.selected_resume;
    const match = stored ? resumes.find(r => r.resumename === stored) : undefined;
    onSelectResume(match ? match.resumename : resumes[0].resumename);
  }, [loadingResumes, resumes, selectedResume, prefsResolved, userPreferences]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (resumeDropdownRef.current && !resumeDropdownRef.current.contains(event.target as Node)) {
        setShowResumeDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (featureMenuRef.current && !featureMenuRef.current.contains(event.target as Node)) {
        setShowFeatureMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadResumes() {
    try {
      const response = await api.listResumes();
      setResumes(response.resumes);
    } catch (error) {
      console.error('Failed to load resumes:', error);
    } finally {
      setLoadingResumes(false);
    }
  }

  function handleReturnToLanding() {
    window.open('https://skillsnap.me', '_blank');
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - fully left justified */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-2">
              <img src="/assets/skillsnap_logo.png" alt="SkillSnap" className="h-8 w-auto" />
              <span className="text-xl font-semibold text-primary-600">SkillSnap</span>
            </a>
          </div>

          {/* Desktop Navigation - fully right justified */}
          <nav className="hidden md:flex items-center space-x-4 ml-auto">
            <FreeGenCounter userPreferences={userPreferences} onUpgrade={onUpgrade} />
            {/* Resume Selector */}
            <div className="relative" ref={resumeDropdownRef}>
              <button
                onClick={() => setShowResumeDropdown(!showResumeDropdown)}
                data-help-target="mainapp.resumes"
                className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                aria-expanded={showResumeDropdown}
                aria-controls="resume-dropdown-menu"
              >
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700 max-w-48 truncate">
                  {loadingResumes ? 'Loading...' : (selectedResume || 'Select Resume')}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {showResumeDropdown && (
                <div id="resume-dropdown-menu" role="menu" className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  {resumes.map((resume, idx) => (
                    <div
                      key={resume.resumename}
                      data-help-target={idx === 0 ? 'mainapp.resumes.resume0' : undefined}
                      className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-100 ${
                        selectedResume === resume.resumename ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => {
                            onSelectResume(resume.resumename);
                            setShowResumeDropdown(false);
                          }}
                          className="w-full text-left truncate"
                        >
                          {resume.resumename}
                        </button>
                        {/* Recruiter Check BR-1: master-resume problems ride along
                            on every generated resume, so they are named right where
                            the user picks the resume. Opens Edit Resume, which shows
                            the same warning and the Scorecard. */}
                        <MasterLintWarning
                          resume={resume}
                          onClick={() => {
                            if (selectedResume !== resume.resumename) onSelectResume(resume.resumename);
                            onEditResume(resume.resumename);
                            setShowResumeDropdown(false);
                          }}
                        />
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // If not selected, select it first then edit
                          if (selectedResume !== resume.resumename) {
                            onSelectResume(resume.resumename);
                          }
                          onEditResume(resume.resumename);
                          setShowResumeDropdown(false);
                        }}
                        data-help-target={idx === 0 ? 'mainapp.resumes.resume0.edit' : undefined}
                        className="p-1 hover:bg-gray-200 rounded ml-2"
                        title="Edit resume"
                        aria-label="Edit resume"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {resumes.length === 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No resumes yet
                    </div>
                  )}
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      onNewResume();
                      setShowResumeDropdown(false);
                    }}
                    data-help-target="mainapp.resumes.addresume"
                    className="flex items-center w-full px-4 py-2 text-sm text-primary-600 hover:bg-gray-100"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Resume
                  </button>
                </div>
              )}
            </div>

            {/* Feature Selector — one menu to reach every major feature */}
            <div className="relative" ref={featureMenuRef}>
              <button
                onClick={() => setShowFeatureMenu(!showFeatureMenu)}
                data-help-target="mainapp.features"
                data-uat="feature-selector"
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                title="Feature Selector"
                aria-label="Feature Selector"
                aria-expanded={showFeatureMenu}
                aria-controls="feature-selector-menu"
              >
                <LayoutGrid className="w-4 h-4 text-gray-600" />
              </button>

              {showFeatureMenu && (
                <div
                  id="feature-selector-menu"
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                >
                  <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Features
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => {
                      if (selectedResume) {
                        onEditResume(selectedResume);
                      } else {
                        onNewResume();
                      }
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.resumeeditor"
                    data-uat="feature-resume-editor"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Edit className="w-4 h-4 mr-2 text-gray-500" />
                    Resume Editor
                    <HotkeyChip>{HOTKEY_CHIP['resume-editor']}</HotkeyChip>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      window.open(window.location.origin, '_blank');
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.jobmanager"
                    data-uat="feature-job-manager"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Briefcase className="w-4 h-4 mr-2 text-gray-500" />
                    Job Manager
                    <HotkeyChip>{HOTKEY_CHIP['job-manager']}</HotkeyChip>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      window.open(`${window.location.origin}/activity-report`, '_blank');
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.activityreport"
                    data-uat="feature-activity-report"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <BarChart3 className="w-4 h-4 mr-2 text-gray-500" />
                    Activity Dashboard
                    <HotkeyChip>{HOTKEY_CHIP['activity-dashboard']}</HotkeyChip>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      onOpenWritingSample();
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.writingsample"
                    data-uat="feature-writing-sample"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Fingerprint className="w-4 h-4 mr-2 text-gray-500" />
                    Writing Sample
                    <HotkeyChip>{HOTKEY_CHIP['writing-sample']}</HotkeyChip>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      window.open(`${window.location.origin}/application-qa`, '_blank');
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.applicationqa"
                    data-uat="feature-application-qa"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MessageSquareText className="w-4 h-4 mr-2 text-gray-500" />
                    Application Q&amp;A
                    <HotkeyChip>{HOTKEY_CHIP['application-qa']}</HotkeyChip>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      window.open(`${window.location.origin}/interview-qa`, '_blank');
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.interviewqa"
                    data-uat="feature-interview-qa"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MessageCircleQuestion className="w-4 h-4 mr-2 text-gray-500" />
                    Interview Q&amp;A
                    <HotkeyChip>{HOTKEY_CHIP['interview-qa']}</HotkeyChip>
                  </button>
                  <hr className="my-1" />
                  <button
                    role="menuitem"
                    onClick={() => {
                      openHelpPanel();
                      setShowFeatureMenu(false);
                    }}
                    data-help-target="mainapp.features.help"
                    data-uat="feature-help"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <HelpCircle className="w-4 h-4 mr-2 text-gray-500" />
                    Help &amp; Tips
                    <HotkeyChip>{HOTKEY_CHIP.help}</HotkeyChip>
                  </button>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-help-target="mainapp.user"
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-expanded={showUserMenu}
                aria-controls="user-dropdown-menu"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm text-gray-700">{user?.username || 'User'}</span>
              </button>

              {showUserMenu && (
                <div id="user-dropdown-menu" role="menu" className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <button
                    role="menuitem"
                    onClick={() => { onOpenSettings(); setShowUserMenu(false); }}
                    data-help-target="mainapp.user.settings"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { handleReturnToLanding(); setShowUserMenu(false); }}
                    data-help-target="mainapp.user.website"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Website
                  </button>
                  <div className="flex items-center px-4 py-2 text-sm text-gray-500" data-help-target="mainapp.user.version">
                    <Info className="w-4 h-4 mr-2" />
                    Version {version.trim()}
                  </div>
                  <hr className="my-1" />
                  <button
                    role="menuitem"
                    onClick={() => { logout(); setShowUserMenu(false); }}
                    data-help-target="mainapp.user.logout"
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                  <hr className="my-1" />
                  <button
                    role="menuitem"
                    onClick={() => { setShowFeedbackModal(true); setShowUserMenu(false); }}
                    data-help-target="mainapp.user.feedback"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <MessageSquareHeart className="w-4 h-4 mr-2" />
                    Provide Feedback
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-2">
            {userPreferences?.status === 'trial' && (
              <button
                onClick={() => { onUpgrade(); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm font-semibold text-amber-600 hover:bg-amber-50 rounded-lg"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade Now
              </button>
            )}

            {/* Resume Selector (mobile) */}
            <div className="px-2 pt-1">
              <div className="flex items-center px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Resumes
              </div>
              {loadingResumes ? (
                <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
              ) : resumes.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No resumes yet</div>
              ) : (
                resumes.map((resume) => (
                  <div
                    key={resume.resumename}
                    className={`flex items-center justify-between w-full px-4 py-2 text-sm rounded-lg ${
                      selectedResume === resume.resumename
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => {
                          onSelectResume(resume.resumename);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full text-left truncate"
                      >
                        {resume.resumename}
                      </button>
                      <MasterLintWarning
                        resume={resume}
                        onClick={() => {
                          if (selectedResume !== resume.resumename) onSelectResume(resume.resumename);
                          onEditResume(resume.resumename);
                          setMobileMenuOpen(false);
                        }}
                      />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedResume !== resume.resumename) {
                          onSelectResume(resume.resumename);
                        }
                        onEditResume(resume.resumename);
                        setMobileMenuOpen(false);
                      }}
                      className="p-1 hover:bg-gray-200 rounded ml-2"
                      title="Edit resume"
                      aria-label="Edit resume"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
              <button
                onClick={() => { onNewResume(); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-primary-600 hover:bg-gray-100 rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Resume
              </button>
            </div>

            {/* Feature Selector (mobile) — mirrors the desktop Feature menu, below Resumes */}
            <hr />
            <div className="px-2 pt-1">
              <div className="flex items-center px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />
                Features
              </div>
              <button
                onClick={() => {
                  if (selectedResume) {
                    onEditResume(selectedResume);
                  } else {
                    onNewResume();
                  }
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Edit className="w-4 h-4 mr-2 text-gray-500" />
                Resume Editor
                <HotkeyChip>{HOTKEY_CHIP['resume-editor']}</HotkeyChip>
              </button>
              <button
                onClick={() => { window.open(window.location.origin, '_blank'); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Briefcase className="w-4 h-4 mr-2 text-gray-500" />
                Job Manager
                <HotkeyChip>{HOTKEY_CHIP['job-manager']}</HotkeyChip>
              </button>
              <button
                onClick={() => { window.open(`${window.location.origin}/activity-report`, '_blank'); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <BarChart3 className="w-4 h-4 mr-2 text-gray-500" />
                Activity Dashboard
                <HotkeyChip>{HOTKEY_CHIP['activity-dashboard']}</HotkeyChip>
              </button>
              <button
                onClick={() => { onOpenWritingSample(); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Fingerprint className="w-4 h-4 mr-2 text-gray-500" />
                Writing Sample
                <HotkeyChip>{HOTKEY_CHIP['writing-sample']}</HotkeyChip>
              </button>
              <button
                onClick={() => { window.open(`${window.location.origin}/application-qa`, '_blank'); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <MessageSquareText className="w-4 h-4 mr-2 text-gray-500" />
                Application Q&amp;A
                <HotkeyChip>{HOTKEY_CHIP['application-qa']}</HotkeyChip>
              </button>
              <button
                onClick={() => { window.open(`${window.location.origin}/interview-qa`, '_blank'); setMobileMenuOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <MessageCircleQuestion className="w-4 h-4 mr-2 text-gray-500" />
                Interview Q&amp;A
                <HotkeyChip>{HOTKEY_CHIP['interview-qa']}</HotkeyChip>
              </button>
            </div>

            <hr />
            <button
              onClick={() => { onAddJobUrl(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Link className="w-4 h-4 mr-2" />
              Add Job from URL
            </button>
            <button
              onClick={() => { onAddJobManual(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <FileText className="w-4 h-4 mr-2" />
              Add Job Manually
            </button>
            <button
              onClick={() => { onAddJobEmail(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Mail className="w-4 h-4 mr-2" />
              Add Job from Email
            </button>
            <hr />
            <button
              onClick={() => { onOpenSettings(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
            <button
              onClick={() => { handleReturnToLanding(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Home className="w-4 h-4 mr-2" />
              Website
            </button>
            <div className="flex items-center px-4 py-2 text-sm text-gray-500">
              <Info className="w-4 h-4 mr-2" />
              Version {version.trim()}
            </div>
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 rounded-lg"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
            <hr />
            <button
              onClick={() => { setShowFeedbackModal(true); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <MessageSquareHeart className="w-4 h-4 mr-2" />
              Provide Feedback
            </button>
          </div>
        </div>
      )}

      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />

    </header>
  );
}
